import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { useAuth } from '@/features/auth/context/AuthContext';
import { useImageUrl } from '@/shared/hooks/useImageUrl';
import { useDropdown } from '@/shared/hooks/useDropdown';
import { PreviewModal } from '@/shared/components/PreviewModal';
import { ContentLanguageCode, FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { Exhibition } from '@/shared/types';
import { sortLanguagesWithFrFirst } from '@/shared/utils/languageUtils';
import phoneIcon from '@/assets/svg/phone_icon.svg';
import chevronIcon from '@/assets/svg/chevron_icon.svg';
import mailIcon from '@/assets/svg/mail_icon.svg';
import logoutIcon from '@/assets/svg/logout_icon.svg';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { museum, museumSlug } = useMuseum();
  const { logout, user } = useAuth();
  const location = useLocation();
  const logoUrl = useImageUrl(museum?.logo);
  const { isOpen: isDropdownOpen, toggle: toggleDropdown, close: closeDropdown, ref: dropdownRef } = useDropdown();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLanguage, setPreviewLanguage] = useState<ContentLanguageCode>('fr');
  /** Langues pour lesquelles il existe du contenu publié (expo ou au moins une exposition) */
  const [previewAvailableLanguages, setPreviewAvailableLanguages] = useState<ContentLanguageCode[]>(['fr']);

  // Récupérer le nom du musée dans la langue principale (français par défaut)
  const museumName = museum?.name?.['fr'] || museum?.name?.['en'] || Object.values(museum?.name || {})[0] || 'Dashboard';

  // Extraire l'ID de l'exposition depuis l'URL si on est sur le détail
  const getExhibitionIdFromPath = (): string | null => {
    // L'URL est de la forme: /{museumSlug}/exhibitions/{exhibitionId}
    const pathParts = location.pathname.split('/').filter(Boolean);
    const exhibitionsIndex = pathParts.findIndex(part => part === 'exhibitions');
    if (exhibitionsIndex !== -1 && pathParts[exhibitionsIndex + 1] && pathParts[exhibitionsIndex + 1] !== 'new') {
      return pathParts[exhibitionsIndex + 1];
    }
    return null;
  };

  const handleLogout = async () => {
    await logout();
    closeDropdown();
  };

  const handlePreview = () => {
    setPreviewModalOpen(true);
  };

  const getPreviewBaseUrl = (): string => {
    if (!museum?.pwaUrlBase) return '';

    // Si on est sur le détail d'une exposition
    const exhibitionId = getExhibitionIdFromPath();
    if (exhibitionId) {
      return `${museum.pwaUrlBase}/exhibition/${exhibitionId}`;
    }

    // Sinon, on est sur la liste des expositions
    return `${museum.pwaUrlBase}/museum`;
  };

  // Charger les langues qui ont du contenu publié (ne pas proposer l'anglais si aucune expo en anglais publiée)
  useEffect(() => {
    if (!museum?.id) return;

    const exhibitionId = getExhibitionIdFromPath();
    const candidateLangs = (museum.langs?.length ? museum.langs : ['fr', 'en']) as ContentLanguageCode[];

    const run = async () => {
      const db = getFirebaseDb();
      if (!db) return;

      try {
        if (exhibitionId) {
          const exhibitionRef = doc(db, FIRESTORE_COLLECTIONS.MUSEUMS, museum.id, FIRESTORE_COLLECTIONS.EXHIBITIONS, exhibitionId);
          const snap = await getDoc(exhibitionRef);
          const exhibition = snap.exists() ? { id: snap.id, ...snap.data() } as Exhibition : null;
          if (!exhibition?.status) {
            setPreviewAvailableLanguages(sortLanguagesWithFrFirst(candidateLangs.filter(lang => lang === 'fr')));
            return;
          }
          const published = candidateLangs.filter(lang => exhibition.status[lang] === 'published');
          setPreviewAvailableLanguages(sortLanguagesWithFrFirst(published.length ? published : ['fr']));
        } else {
          const exhibitionsRef = collection(db, FIRESTORE_COLLECTIONS.MUSEUMS, museum.id, FIRESTORE_COLLECTIONS.EXHIBITIONS);
          const q = query(exhibitionsRef, orderBy('order', 'asc'));
          const snapshot = await getDocs(q);
          const exhibitions = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Exhibition));
          const hasPublishedInLang = (lang: ContentLanguageCode) =>
            exhibitions.some(ex => ex.status?.[lang] === 'published');
          const published = candidateLangs.filter(hasPublishedInLang);
          setPreviewAvailableLanguages(sortLanguagesWithFrFirst(published.length ? published : ['fr']));
        }
      } catch {
        setPreviewAvailableLanguages(['fr']);
      }
    };

    run();
  }, [museum?.id, museum?.langs, location.pathname]);

  // Garder la langue de prévisualisation dans la liste disponible
  useEffect(() => {
    if (!previewAvailableLanguages.includes(previewLanguage)) {
      setPreviewLanguage(previewAvailableLanguages[0] ?? 'fr');
    }
  }, [previewAvailableLanguages, previewLanguage]);

  return (
    <div className="dashboard-layout">
      <main className="main-content">
        <header className="main-header">
          <div className="header-left">
            {logoUrl ? (
              <Link to={`/${museumSlug}/`}>
                <img
                  src={logoUrl}
                  alt={museumName}
                  className="museum-logo"
                />
              </Link>
            ) : (
              <h2>{museumName}</h2>
            )}
          </div>
          <div className="header-right">
            {museum?.pwaUrlBase && (
              <button className="preview-btn" onClick={handlePreview}>
                <img src={phoneIcon} alt={t('exhibitions.preview')} className="phone-icon" />
                <span>{t('exhibitions.preview')}</span>
              </button>
            )}
            <div className="account-dropdown" ref={dropdownRef}>
              <button
                className="account-btn"
                onClick={toggleDropdown}
              >
                <span>{t('auth.myAccount')}</span>
                <img
                  src={chevronIcon}
                  alt=""
                  className={`chevron-icon ${isDropdownOpen ? 'rotated' : ''}`}
                />
              </button>
              {isDropdownOpen && (
                <div className="dropdown-menu account-menu">
                  <div className="account-menu-email">
                    {user?.email
                      ? (user.email.includes('#') ? user.email.split('#')[1] : user.email)
                      : t('common.user')}
                  </div>
                  <div className="account-menu-separator" />
                  <a
                    href="mailto:support@oratoria-audioguide.com"
                    className="account-menu-link"
                    onClick={closeDropdown}
                  >
                    <span className="account-menu-icon">
                      <img src={mailIcon} alt="" aria-hidden />
                    </span>
                    <span>{t('auth.contactOratoria')}</span>
                  </a>
                  <div className="account-menu-separator" />
                  <button type="button" className="account-menu-logout" onClick={handleLogout}>
                    <span className="account-menu-icon">
                      <img src={logoutIcon} alt="" aria-hidden />
                    </span>
                    <span>{t('auth.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <div className="content">
          <div className="content-inner">{children}</div>
        </div>
      </main>
      {museum?.pwaUrlBase && (
        <PreviewModal
          isOpen={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          previewUrl={getPreviewBaseUrl()}
          selectedLanguage={previewLanguage}
          onLanguageChange={(lang) => {
            setPreviewLanguage(lang as ContentLanguageCode);
          }}
          availableLanguages={previewAvailableLanguages}
        />
      )}
    </div>
  );
};

